import cn from 'classnames';
import { FC, memo, useMemo } from 'react';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { MedicalImageInterface } from '@/shared/config';
import { SlicesMIR } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { Group, MedicalImagesGroups, PathologyGroup } from '@/entities/assets';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';

import { useFDAEnvironment } from '@/features/fdaRestrictions';
import { maskFiltersModel } from '@/features/renderMasks';

import { MedicalImageGroupSection } from '../MedicalImageGroupSection/MedicalImageGroupSection';

import styles from './MedicalImagesGroup.module.scss';

type MedicalImagesGroupProps = {
  toothID: string;
  selectedMedicalImages?: MedicalImageInterface[];
  groups: (Group | PathologyGroup)[];
  toggleSelected?: (medicalImage: MedicalImageInterface) => void;
  className?: string;
};

export const MedicalImagesGroup: FC<MedicalImagesGroupProps> = memo((props) => {
  const { className, selectedMedicalImages, toggleSelected, groups, toothID } =
    props;

  const dispatch = useAppDispatch();

  const { isFDA, masksVisible } = useFDAEnvironment();

  const hidePerioMasksAndSlicesFF = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Hide_PerioMasksAndSlices,
    ),
  );

  const { isConditionsMasksHidden, isCBCTMasksHidden } = useAppSelector(
    maskFiltersModel.selectors.selectMasksFlags,
  );

  const showPerioMasksAndSlices = useMemo(() => {
    if (isFDA) {
      return masksVisible;
    }

    return (
      !hidePerioMasksAndSlicesFF &&
      !isConditionsMasksHidden &&
      !isCBCTMasksHidden
    );
  }, [
    hidePerioMasksAndSlicesFF,
    isFDA,
    masksVisible,
    isConditionsMasksHidden,
    isCBCTMasksHidden,
  ]);

  const hideCariesSigns = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Hide_CariesSigns,
    ),
  );

  const filteredGroups = groups.filter((group) => {
    if (!group?.guides?.length && !group?.medicalImages?.length) {
      return false;
    }

    if (group.type === MedicalImagesGroups.Caries) {
      return !hideCariesSigns && !isFDA;
    }

    if (group.type === MedicalImagesGroups.PeriapicalLesion) {
      return showPerioMasksAndSlices;
    }

    return true;
  });

  const openMIRenderModal = (assetID: string) => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.MIRenderModal,
        data: { assetID, toothID },
      }),
    );
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-section="medical-images-group"
    >
      {filteredGroups.map((group, index) => (
        <SlicesMIR
          key={`${group.type}_${index}`}
          toothID={toothID}
          onClick={openMIRenderModal}
        >
          <MedicalImageGroupSection
            key={`${group.type}_${index}`}
            toothID={toothID}
            group={group}
            selectedMedicalImages={selectedMedicalImages}
            toggleSelected={toggleSelected}
          />
        </SlicesMIR>
      ))}
    </div>
  );
});
